'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CloseIcon } from '@/assets/icons';
import { Button, Dialog, H2, H3, Li, P, Span, Ul, cn } from '@/components/ui';
import type { CookieConsentProps, CookieLayout, CookieType } from '@/constants/cookie';
import { defaultCookies } from '@/constants/cookie';
import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { commitCookieConsent } from '@/redux/store/cookie';
import { Toggle } from '@/components/widget';

function mergeConsentFromSaved(
  cookieList: CookieType[],
  saved: Record<string, boolean> | null,
): Record<string, boolean> {
  const next: Record<string, boolean> = {};
  for (const c of cookieList) {
    next[c.key] = c.required ?? false;
  }
  if (!saved) return next;
  for (const c of cookieList) {
    if (Object.prototype.hasOwnProperty.call(saved, c.key)) {
      next[c.key] = saved[c.key] ?? false;
    }
  }
  return next;
}

export const CookieConsent: React.FC<CookieConsentProps> = ({
  cookies = defaultCookies,
  detailedLayout = 'modal',
}) => {
  const dispatch = useAppDispatch();
  const hasCompletedBanner = useAppSelector((s) => s.cookie.hasCompletedBanner);
  const savedConsent = useAppSelector((s) => s.cookie.consent);

  const [consent, setConsent] = useState<Record<string, boolean>>(() =>
    mergeConsentFromSaved(cookies, savedConsent),
  );
  const [showDetails, setShowDetails] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);

  const showMiniBar = !hasCompletedBanner;

  useEffect(() => {
    setConsent(mergeConsentFromSaved(cookies, savedConsent));
  }, [cookies, savedConsent]);

  const acceptAll = useCallback(() => {
    const allConsent = cookies.reduce(
      (acc, cookie) => {
        acc[cookie.key] = true;
        return acc;
      },
      {} as Record<string, boolean>,
    );
    setConsent(allConsent);
    dispatch(commitCookieConsent(allConsent));
    setShowDetails(false);
    dialogRef.current?.close();
  }, [cookies, dispatch]);

  const rejectAll = useCallback(() => {
    const rejectConsent = cookies.reduce(
      (acc, cookie) => {
        acc[cookie.key] = cookie.required ?? false;
        return acc;
      },
      {} as Record<string, boolean>,
    );
    setConsent(rejectConsent);
    dispatch(commitCookieConsent(rejectConsent));
    setShowDetails(false);
    dialogRef.current?.close();
  }, [cookies, dispatch]);

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (showDetails) {
      if (!el.open) {
        try {
          el.showModal();
        } catch {
          /* already open */
        }
      }
    } else if (el.open) {
      el.close();
    }
  }, [showDetails]);

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    const onDialogClose = () => setShowDetails(false);
    el.addEventListener('close', onDialogClose);
    return () => el.removeEventListener('close', onDialogClose);
  }, []);

  const closeDetails = useCallback(() => {
    dialogRef.current?.close();
    setShowDetails(false);
  }, []);

  const doneWithPreferences = useCallback(() => {
    dispatch(commitCookieConsent(consent));
    dialogRef.current?.close();
    setShowDetails(false);
  }, [consent, dispatch]);

  const sheetPlacement = (layout: CookieLayout) =>
    cn(
      'cookie-consent-sheet',
      layout === 'sidebar' ? 'cookie-consent-sheet--sidebar' : 'cookie-consent-sheet--modal',
    );

  return (
    <>
      {showMiniBar && (
        <aside className="cookie-consent-banner" aria-label="Cookie consent">
          <div className="cookie-consent-banner__inner">
            <P className="cookie-consent-banner__copy m-0 p-0">
              We use cookies and similar technologies to run this site, understand performance, and
              improve your experience. You can manage preferences at any time.
            </P>
            <div className="cookie-consent-banner__actions">
              <Button
                type="button"
                onClick={() => setShowDetails(true)}
                className={cn('btn', 'cookie-consent-link')}
              >
                Manage
              </Button>
              <Button
                type="button"
                onClick={rejectAll}
                className={cn('btn', 'cookie-consent-btn--secondary')}
              >
                Reject non-essential
              </Button>
              <Button
                type="button"
                onClick={acceptAll}
                className={cn('btn', 'cookie-consent-btn--primary')}
              >
                Accept all
              </Button>
            </div>
          </div>
        </aside>
      )}

      <Dialog
        ref={dialogRef}
        className={sheetPlacement(detailedLayout)}
        aria-labelledby="cookie-prefs-title"
      >
        <div className="cookie-consent-sheet__header">
          <div className="cookie-consent-sheet__header-row">
            <div className="min-w-0 flex-1">
              <H2 id="cookie-prefs-title" className="cookie-consent-sheet__title m-0 p-0">
                Cookie preferences
              </H2>
              <P className="cookie-consent-sheet__subtitle m-0 p-0">
                Choose which categories we may use. Essential cookies are always on because the
                site cannot work without them.
              </P>
            </div>
            <Button
              type="button"
              onClick={closeDetails}
              className={cn('btn', 'cookie-consent-sheet__close')}
              aria-label="Close cookie preferences"
            >
              <CloseIcon className="size-5 shrink-0" />
            </Button>
          </div>
        </div>

        <div className="cookie-consent-sheet__body">
          <Ul className="ul m-0 list-none p-0">
            {cookies.map((cookie) => (
              <Li
                key={cookie.key}
                className={cn(
                  'li cookie-consent-row',
                  !cookie.required && consent[cookie.key] && 'cookie-consent-row--on',
                )}
              >
                <div className="min-w-0 flex-1">
                  <H3 className="cookie-consent-row__title m-0 p-0">{cookie.title}</H3>
                  <P className="cookie-consent-row__desc m-0 p-0">{cookie.description}</P>
                </div>
                <div className="cookie-consent-row__toggle">
                  {cookie.required ? (
                    <Span className="cookie-consent-row__required">Always on</Span>
                  ) : (
                    <Toggle
                      size="sm"
                      checked={consent[cookie.key]}
                      onChange={(next) => setConsent((prev) => ({ ...prev, [cookie.key]: next }))}
                      label={cookie.title}
                      wrapperClassName="max-sm:w-full sm:justify-end"
                      className="shrink-0"
                    />
                  )}
                </div>
              </Li>
            ))}
          </Ul>
        </div>

        <div className="cookie-consent-sheet__footer">
          <Button
            type="button"
            onClick={doneWithPreferences}
            className={cn('btn', 'cookie-consent-btn--secondary', 'w-full sm:w-auto')}
          >
            Done
          </Button>
          <Button
            type="button"
            onClick={rejectAll}
            className={cn('btn', 'cookie-consent-btn--secondary', 'w-full sm:w-auto')}
          >
            Reject non-essential
          </Button>
          <Button
            type="button"
            onClick={acceptAll}
            className={cn('btn', 'cookie-consent-btn--primary', 'w-full sm:w-auto')}
          >
            Accept all
          </Button>
        </div>
      </Dialog>
    </>
  );
};
